import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Edit, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", start_date: "", class_date: "", status: "upcoming" };

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/batches").then((r) => setBatches(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.patch(`/admin/batches/${editing.id}`, form);
      else await api.post("/admin/batches", form);
      toast.success("Batch saved");
      setOpen(false);
      load();
    } catch { toast.error("Failed to save batch"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete batch ini?")) return;
    await api.delete(`/admin/batches/${id}`);
    load();
  };

  return (
    <AdminLayout title="Batches">
      <div className="flex justify-between items-end gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Batches</h1>
          <p className="mt-1 text-muted-foreground">Manage class batches.</p>
        </div>
        <Button onClick={openAdd} className="btn-glow" data-testid="add-batch-btn"><Plus className="h-4 w-4" /> Add Batch</Button>
      </div>

      <Card className="glass-card">
        <CardContent className="p-0">
          <div className="table-wrap">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Class Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Restricted Content</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b) => {
                  const contentTotal = (b.recording_count || 0) + (b.module_count || 0) + (b.note_count || 0);
                  return (
                    <TableRow key={b.id} data-testid={`batch-row-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell>{b.class_date || "—"}</TableCell>
                      <TableCell><StatusBadge status={b.status} label={b.status} /></TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{b.student_count || 0}</span>
                      </TableCell>
                      <TableCell>
                        {contentTotal === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 text-[11px]">
                            {b.recording_count > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-teal/15 border border-teal/30 text-teal font-medium">
                                {b.recording_count} recordings
                              </span>
                            )}
                            {b.module_count > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-primary/15 border border-primary/30 text-primary font-medium">
                                {b.module_count} modules
                              </span>
                            )}
                            {b.note_count > 0 && (
                              <span className="px-2 py-0.5 rounded-md bg-gold/15 border border-gold/30 text-gold font-medium">
                                {b.note_count} notes
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(b.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit Batch" : "Add Batch"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-batch-name" /></div>
            <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Class Date</Label><Input type="date" value={form.class_date} onChange={(e) => setForm({ ...form, class_date: e.target.value })} /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-batch-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
